/* eslint-disable react-hooks/exhaustive-deps */
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ref,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  getBlob,
} from "firebase/storage";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  updateDoc,
  getDoc,
  limit,
  deleteField,
} from "firebase/firestore";

import { useFirebase } from "../FirebaseContext";
import {
  generateThumbnail,
  generateThumbnailFilename,
  isThumbnailSupported,
} from "../../utils/files/thumbnailUtils";

import {
  deleteFolderRecursively,
  renameFolder,
  checkNameExists,
  moveFile,
} from "../../utils/firestore/query";
import useZipDownload from "../../hooks/useZipDownload";
import { formatUploadErrorMessage } from "../../utils/files/uploadErrors";

const FileActionsContext = createContext();

export const FileActionsProvider = ({ children, isPublicContext = false }) => {
  const { storage, db, app, tenant, auth, folderPrefix } = useFirebase();

  // UPLOAD FILE
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileName, setUploadFileName] = useState("");
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [isUploadMinimized, setIsUploadMinimized] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.__IS_UPLOADING__ = uploading;
    }
  }, [uploading]);

  // NEW FOLDER
  const [newFolderDialogOpen, setNewFolderDialogOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Initialize currentFolder from URL search parameters (authenticated) or internal state (public)
  const [publicFolder, setPublicFolder] = useState("");
  const currentFolder = useMemo(() => {
    if (isPublicContext) return publicFolder;
    const params = new URLSearchParams(location.search);
    const rawPath = params.get("path") || "";
    return rawPath.replace(/^\/+|\/+$/g, "");
  }, [isPublicContext, publicFolder, location.search]);
  // In-memory cache for folder listings to make folder navigation instant (0ms)
  const folderCacheRef = useRef(new Map());

  // Clear folder cache when tenant changes
  useEffect(() => {
    folderCacheRef.current.clear();
  }, [tenant]);

  // RENAME DIALOG
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameFile, setRenameFile] = useState(null);
  const [renaming, setRenaming] = useState(false);

  // SNACKBAR MESSAGE
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  // CONFIRMATION MODAL
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [confirmationModalConfig, setConfirmationModalConfig] = useState({
    title: "",
    message: "",
    onConfirm: () => {},
    severity: "warning",
  });

  const showConfirmation = useCallback((
    title,
    message,
    onConfirm,
    severity = "warning",
  ) => {
    setConfirmationModalConfig({
      title,
      message,
      onConfirm,
      severity,
    });
    setConfirmationModalOpen(true);
  }, []);

  // DELETING STATE
  const [isDeleting, setIsDeleting] = useState(false);

  // FILE SELECTION
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);

  const handleFileSelection = useCallback((file) => {
    setSelectedFiles((prevSelected) => {
      if (prevSelected.includes(file.fullPath)) {
        return prevSelected.filter((path) => path !== file.fullPath);
      } else {
        return [...prevSelected, file.fullPath];
      }
    });
  }, []);

  const searchFilesAndFolders = useCallback(
    async (searchQuery) => {
      if (!searchQuery) {
        setSearchResults([]);
        return;
      }

      setLoadingSearch(true);
      try {
        const filesCollection = collection(db, "files");
        const foldersCollection = collection(db, "folders");

        const lowerCaseSearchQuery = searchQuery.toLowerCase();

        const filesQuery = query(
          filesCollection,
          where("name_lowercase", ">=", lowerCaseSearchQuery),
          where("name_lowercase", "<=", lowerCaseSearchQuery + "\uf8ff"),
        );

        const foldersQuery = query(
          foldersCollection,
          where("name_lowercase", ">=", lowerCaseSearchQuery),
          where("name_lowercase", "<=", lowerCaseSearchQuery + "\uf8ff"),
        );

        const [filesSnapshot, foldersSnapshot] = await Promise.all([
          getDocs(filesQuery),
          getDocs(foldersQuery),
        ]);

        const results = [];
        const seen = new Set();

        filesSnapshot.forEach((doc) => {
          const data = { id: doc.id, ...doc.data(), type: "file" };
          const uniqueId = `${data.id}-${data.type}`;
          if (!seen.has(uniqueId)) {
            results.push(data);
            seen.add(uniqueId);
          }
        });
        foldersSnapshot.forEach((doc) => {
          const data = { id: doc.id, ...doc.data(), type: "folder" };
          const uniqueId = `${data.id}-${data.type}`;
          if (!seen.has(uniqueId)) {
            results.push(data);
            seen.add(uniqueId);
          }
        });

        setSearchResults(results);
      } catch (error) {
        console.error("Error searching files and folders:", error);
        showSnackbar(
          "Erro ao buscar arquivos e pastas: " + error.message,
          "error",
        );
      } finally {
        setLoadingSearch(false);
      }
    },
    [db, showSnackbar],
  );

  const handleSelectAll = useCallback(() => {
    if (selectedFiles.length === files.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(files.map((file) => file.fullPath));
    }
  }, [selectedFiles.length, files]);

  const clearSelection = useCallback(() => {
    setSelectedFiles([]);
  }, []);

  // Use the useZipDownload hook
  const {
    downloadZip,
    isZipping,
    zipProgress,
    error: zipError,
  } = useZipDownload(storage); // Pass the storage instance here

  const downloadSelectedAsZip = useCallback(async () => {
    if (selectedFiles.length === 0) {
      showSnackbar("Nenhum arquivo selecionado para download.", "warning");
      return;
    }

    // Filter out folders from the selection if any, and map to expected format
    const filesToDownload = files
      .filter(
        (file) => selectedFiles.includes(file.fullPath) && file.type === "file",
      )
      .map((file) => ({
        fullPath: file.fullPath,
        name: file.name,
      }));

    if (filesToDownload.length === 0) {
      showSnackbar(
        "Nenhum arquivo válido selecionado para download.",
        "warning",
      );
      return;
    }

    try {
      showSnackbar("Preparando download ZIP...", "info");
      // Call the client-side zip download function
      await downloadZip(filesToDownload, "drive_download.zip");
      showSnackbar("Download ZIP iniciado!", "success");
      clearSelection(); // Clear selection after initiating download
    } catch (error) {
      console.error("Erro ao gerar ZIP no cliente:", error);
      showSnackbar("Erro ao gerar ZIP: " + error.message, "error");
    }
  }, [selectedFiles, files, showSnackbar, clearSelection, downloadZip]);

  const listAllFiles = useCallback(
    async (targetFolderOrId = null, options = {}) => {
      const forceRefresh = options?.forceRefresh === true;
      const folderToQuery =
        typeof targetFolderOrId === "string"
          ? targetFolderOrId
          : (currentFolder || "");
      const cleanFolderToQuery = (folderToQuery || "").replace(/^\/+|\/+$/g, "");
      const cacheKey = `${isPublicContext ? "pub:" : "auth:"}${cleanFolderToQuery}`;

      // If already cached and not force refreshing, render instantly (0ms)
      if (!forceRefresh && folderCacheRef.current.has(cacheKey)) {
        setFiles(folderCacheRef.current.get(cacheKey));
        setLoadingFiles(false);
        return;
      }

      setLoadingFiles(true);
      try {
        if (!db) {
          console.warn(
            "FileActionsContext: Firestore db not available for listAllFiles.",
          );
          setLoadingFiles(false);
          return;
        }
        const filesCollection = collection(db, "files");
        const foldersCollection = collection(db, "folders");

        if (isPublicContext) {
          let currentFolderId = null;

          if (typeof targetFolderOrId === "string" && targetFolderOrId) {
            try {
              const docRef = doc(db, "folders", targetFolderOrId); // Corrected syntax
              const directDocSnapshot = await getDoc(docRef); // Corrected syntax
              if (
                directDocSnapshot.exists &&
                directDocSnapshot.data().public === true
              ) {
                console.log(
                  "DIAG: Direct get by ID succeeded for 'Atena' (7HJWZHjvbZIjmOr5lpwt) and it's public.",
                );
                currentFolderId = directDocSnapshot.id;
              } else {
                console.warn(
                  "DIAG: Direct get by ID failed or folder 'Atena' not public for 7HJWZHjvbZIjmOr5lpwt.",
                );
              }
            } catch (e) {
              console.error("DIAG: Error during direct get by ID:", e.message);
            }
          } else {
            showSnackbar(
              "A pasta que você está tentando acessar não é pública.",
              "error",
            );
            setLoadingFiles(false);
            setFiles([]);
            return;
          }

          // Perform two queries to simulate an OR condition:
          // 1. Items that are explicitly public themselves AND are in the current folder.
          // 2. Items that are not explicitly public, but whose immediate parent is the currentFolder (which we confirmed is public).

          // Query 1: Items that are explicitly public AND reside in the current folder
          const q1Files = query(
            filesCollection,
            where("folder", "==", currentFolder || ""),
            where("public", "==", true),
            orderBy("name"),
          );
          const q1Folders = query(
            foldersCollection,
            where("folder", "==", currentFolder || ""),
            where("public", "==", true),
            orderBy("name"),
          );

          // Query 2: Items that reside in the current folder AND their immediateParentFolderId matches the currentFolder's ID
          let q2Files = null;
          let q2Folders = null;

          if (currentFolderId) {
            // Only run this if we have a specific currentFolder with an ID
            q2Files = query(
              filesCollection,
              where("folder", "==", currentFolder || ""),
              where("immediateParentFolderId", "==", currentFolderId),
              orderBy("name"),
            );
            q2Folders = query(
              foldersCollection,
              where("folder", "==", currentFolder || ""),
              where("immediateParentFolderId", "==", currentFolderId),
              orderBy("name"),
            );
          }

          const [
            q1FilesSnapshot,
            q1FoldersSnapshot,
            q2FilesSnapshot,
            q2FoldersSnapshot,
          ] = await Promise.all([
            getDocs(q1Files),
            getDocs(q1Folders),
            q2Files ? getDocs(q2Files) : Promise.resolve({ docs: [] }),
            q2Folders ? getDocs(q2Folders) : Promise.resolve({ docs: [] }),
          ]);

          const mergedFiles = new Map(); // Use Map to deduplicate by doc.id
          q1FilesSnapshot.forEach((doc) =>
            mergedFiles.set(doc.id, {
              id: doc.id,
              ...doc.data(),
              type: "file",
            }),
          );
          q2FilesSnapshot.forEach((doc) =>
            mergedFiles.set(doc.id, {
              id: doc.id,
              ...doc.data(),
              type: "file",
            }),
          ); // Ensure type is explicitly set

          const mergedFolders = new Map(); // Use Map to deduplicate by doc.id
          q1FoldersSnapshot.forEach((doc) =>
            mergedFolders.set(doc.id, {
              id: doc.id,
              ...doc.data(),
              type: "folder",
            }),
          );
          q2FoldersSnapshot.forEach((doc) =>
            mergedFolders.set(doc.id, {
              id: doc.id,
              ...doc.data(),
              type: "folder",
            }),
          ); // Ensure type is explicitly set

          // Convert Maps to Arrays, save to cache and set state
          const publicItems = [
            ...Array.from(mergedFolders.values()),
            ...Array.from(mergedFiles.values()),
          ].filter((item) => item.type === "file");
          folderCacheRef.current.set(cacheKey, publicItems);
          setFiles(publicItems);
        } else {
          // --- Original Logic for Authenticated Context (or non-public) ---
          const folderToQuery =
            typeof targetFolderOrId === "string"
              ? targetFolderOrId
              : (currentFolder || "");
          const cleanFolderToQuery = (folderToQuery || "").replace(/^\/+|\/+$/g, "");

          let filesQuery, foldersQuery;

          if (cleanFolderToQuery) {
            filesQuery = query(
              filesCollection,
              where("folder", "==", cleanFolderToQuery),
              orderBy("name"),
            );
            foldersQuery = query(
              foldersCollection,
              where("folder", "==", cleanFolderToQuery),
              orderBy("name"),
            );
          } else {
            filesQuery = query(
              filesCollection,
              where("folder", "==", ""),
              orderBy("name"),
            );
            foldersQuery = query(
              foldersCollection,
              where("folder", "==", ""),
              orderBy("name"),
            );
          }

          const [filesSnapshot, foldersSnapshot] = await Promise.all([
            getDocs(filesQuery),
            getDocs(foldersQuery),
          ]);

          const filesList = [];
          const foldersList = [];

          filesSnapshot.forEach((doc) => {
            const data = doc.data();
            const rawFullPath =
              data.fullPath ||
              (data.folder ? `${data.folder}/${data.name}` : data.name) ||
              "";
            const cleanFullPath = rawFullPath.replace(/^\/+|\/+$/g, "");
            const cleanFolder = (data.folder || "").replace(/^\/+|\/+$/g, "");
            filesList.push({
              id: doc.id,
              name: data.name,
              fullPath: cleanFullPath,
              folder: cleanFolder,
              type: data.type || "file", // Default to 'file' if not explicitly set
              contentType: data.contentType,
              size: data.size,
              thumbnailUrl: data.thumbnailUrl,
              originalUrl: data.originalUrl,
              createdAt: data.createdAt,
              metadata: {
                contentType: data.contentType,
                size: data.size,
              },
            });
          });

          foldersSnapshot.forEach((doc) => {
            const data = doc.data();
            const rawFullPath =
              data.fullPath ||
              (data.folder ? `${data.folder}/${data.name}` : data.name) ||
              "";
            const cleanFullPath = rawFullPath.replace(/^\/+|\/+$/g, "");
            const cleanFolder = (data.folder || "").replace(/^\/+|\/+$/g, "");
            foldersList.push({
              id: doc.id,
              name: data.name,
              fullPath: cleanFullPath,
              folder: cleanFolder,
              type: "folder",
              thumbnailUrl: data.thumbnailUrl || null,
              coverPosition: data.coverPosition || "center 50%",
              public: data.public || false,
              createdAt: data.createdAt,
              updatedAt: data.updatedAt,
            });
          });

          const combined = [...foldersList, ...filesList];
          folderCacheRef.current.set(cacheKey, combined);
          setFiles(combined);
        }
      } catch (error) {
        console.error("Erro ao listar arquivos:", error);
        showSnackbar("Erro ao listar arquivos: " + error.message, "error");
      } finally {
        setLoadingFiles(false);
      }
    },
    [currentFolder, db, isPublicContext, showSnackbar],
  );

  // Update current folder by updating the URL (authenticated) or internal state (public)
  const setCurrentFolder = useCallback(
    (folderPath) => {
      const cleanPath = (folderPath || "").replace(/^\/+|\/+$/g, "");
      setSelectedFiles([]);
      if (isPublicContext) {
        setPublicFolder(cleanPath);
      } else {
        if (cleanPath) {
          const encodedPath = cleanPath
            .split("/")
            .map((part) => encodeURIComponent(part))
            .join("/");
          navigate(`/${tenant}?path=${encodedPath}`);
        } else {
          navigate(`/${tenant}`);
        }
      }
    },
    [isPublicContext, tenant, navigate],
  );

  // Clear selection on folder navigation
  useEffect(() => {
    setSelectedFiles([]);
  }, [currentFolder]);

  const handleFileUpload = useCallback(
    async (event) => {
      const inputElement = event?.target;
      const filesToUpload = Array.from(inputElement?.files || []);
      if (filesToUpload.length === 0) return;

      setUploadModalOpen(true);
      setUploading(true);
      setUploadProgress(0);

      const initialQueue = filesToUpload.map((file, index) => ({
        id: `${file.name}-${index}-${Date.now()}`,
        name: file.name,
        size: file.size,
        progress: 0,
        status: "pending", // "pending" | "uploading" | "completed" | "error"
        error: null,
        file,
      }));

      setUploadQueue(initialQueue);
      setUploadFileName(
        filesToUpload.length === 1
          ? filesToUpload[0].name
          : `${filesToUpload.length} arquivos`,
      );

      const updateItemInQueue = (fileId, patch) => {
        setUploadQueue((prevQueue) => {
          const nextQueue = prevQueue.map((item) =>
            item.id === fileId ? { ...item, ...patch } : item,
          );
          // Recalculate global upload progress
          const totalProgress = nextQueue.reduce(
            (acc, item) => acc + (item.progress || 0),
            0,
          );
          const overall = Math.round(totalProgress / nextQueue.length);
          setUploadProgress(overall);
          return nextQueue;
        });
      };

      let successfulCount = 0;
      let failedCount = 0;
      const failedItems = [];
      const CONCURRENCY_LIMIT = 3;

      // Concurrency queue runner
      let currentIndex = 0;
      const uploadSingleFile = async (queueItem) => {
        const { id, file } = queueItem;
        updateItemInQueue(id, { status: "uploading", progress: 5 });

        try {
          const nameExists = await checkNameExists(db, currentFolder, file.name);
          if (nameExists) {
            const duplicateMsg = `Já existe um arquivo ou pasta com o nome "${file.name}"`;
            updateItemInQueue(id, {
              status: "error",
              error: duplicateMsg,
              rawError: duplicateMsg,
              progress: 100,
            });
            failedItems.push({ name: file.name, error: duplicateMsg, rawError: duplicateMsg });
            failedCount++;
            return;
          }

          const filePath = currentFolder
            ? `${currentFolder}/${file.name}`
            : file.name;
          const fileRef = ref(storage, filePath);

          // Pre-generate thumbnail in parallel with file upload for maximum speed
          let thumbnailPromise = null;
          if (isThumbnailSupported(file.type, file.name)) {
            thumbnailPromise = generateThumbnail(file).catch((err) => {
              console.warn("Falha ao gerar thumbnail para", file.name, err);
              return null;
            });
          }

          // Upload original file
          const uploadTask = uploadBytesResumable(fileRef, file);

          await new Promise((resolve, reject) => {
            uploadTask.on(
              "state_changed",
              (snapshot) => {
                if (snapshot.totalBytes > 0) {
                  const pct = Math.round(
                    (snapshot.bytesTransferred / snapshot.totalBytes) * 80,
                  );
                  updateItemInQueue(id, { progress: Math.min(80, 5 + pct) });
                }
              },
              (err) => reject(err),
              () => resolve(),
            );
          });

          updateItemInQueue(id, { progress: 85 });
          const originalUrl = null;

          let thumbnailUrl = null;
          if (thumbnailPromise) {
            const thumbnailBlob = await thumbnailPromise;
            if (thumbnailBlob) {
              try {
                const thumbnailFilename = generateThumbnailFilename(file.name);
                const thumbnailPath = `thumbnails/${
                  currentFolder ? `${currentFolder}/` : ""
                }${thumbnailFilename}`;
                const thumbnailRef = ref(storage, thumbnailPath);
                await uploadBytes(thumbnailRef, thumbnailBlob);
                thumbnailUrl = await getDownloadURL(thumbnailRef);
              } catch (thumbUploadErr) {
                console.warn("Falha no upload do thumbnail:", thumbUploadErr);
              }
            }
          }

          updateItemInQueue(id, { progress: 95 });

          // Save metadata in Firestore
          const fileData = {
            name: file.name,
            name_lowercase: file.name.toLowerCase(),
            fullPath: filePath,
            folder: currentFolder || "",
            type: "file",
            contentType: file.type || "application/octet-stream",
            size: file.size,
            originalUrl: originalUrl,
            thumbnailUrl: thumbnailUrl,
            tenant: tenant || "default",
            createdBy: auth?.currentUser?.email || "unknown",
            userId: auth?.currentUser?.uid || null,
            createdAt: new Date(),
            updatedAt: new Date(),
          };

          await addDoc(collection(db, "files"), fileData);
          updateItemInQueue(id, { status: "completed", progress: 100 });
          successfulCount++;
        } catch (err) {
          console.error(`Erro ao enviar arquivo ${file.name}:`, err);
          const friendlyMessage = formatUploadErrorMessage(err);
          const rawMessage = err?.message || String(err);
          updateItemInQueue(id, {
            status: "error",
            error: friendlyMessage,
            rawError: rawMessage,
            progress: 100,
          });
          failedItems.push({ name: file.name, error: friendlyMessage, rawError: rawMessage });
          failedCount++;
        }
      };

      const workers = Array.from(
        { length: Math.min(CONCURRENCY_LIMIT, initialQueue.length) },
        async () => {
          while (currentIndex < initialQueue.length) {
            const item = initialQueue[currentIndex++];
            await uploadSingleFile(item);
          }
        },
      );

      try {
        await Promise.all(workers);

        if (successfulCount > 0 && failedCount === 0) {
          showSnackbar(
            `${successfulCount} arquivo(s) enviado(s) com sucesso!`,
            "success",
          );
          folderCacheRef.current.clear();
          listAllFiles(currentFolder, { forceRefresh: true });
        } else if (successfulCount > 0 && failedCount > 0) {
          showSnackbar(
            `${successfulCount} arquivo(s) enviado(s), mas ${failedCount} com falha. Verifique os erros no painel de upload.`,
            "warning",
          );
          folderCacheRef.current.clear();
          listAllFiles(currentFolder, { forceRefresh: true });
        } else if (failedCount > 0) {
          if (failedCount === 1 && failedItems.length > 0) {
            showSnackbar(
              `Falha ao enviar "${failedItems[0].name}": ${failedItems[0].error}`,
              "error",
            );
          } else {
            showSnackbar(
              `Falha ao enviar ${failedCount} arquivo(s). Verifique os erros no painel de upload.`,
              "error",
            );
          }
        }
      } catch (error) {
        console.error("Upload error:", error);
        const friendlyErr = formatUploadErrorMessage(error);
        showSnackbar("Erro durante o upload: " + friendlyErr, "error");
      } finally {
        setUploading(false);
        setUploadProgress(100);
        // Only auto-close if all uploads succeeded without errors!
        if (failedCount === 0) {
          setTimeout(() => {
            setUploadModalOpen(false);
          }, 1500);
        } else {
          // If there were any errors, keep the dock open and expanded so the user sees the errors!
          setIsUploadMinimized(false);
        }
        if (inputElement) {
          try {
            inputElement.value = "";
          } catch (e) {
            // ignore
          }
        }
      }
    },
    [db, storage, currentFolder, tenant, auth, showSnackbar, listAllFiles],
  );

  const handleDeleteSelectedFiles = useCallback(() => {
    if (selectedFiles.length === 0) {
      showSnackbar("Nenhum arquivo ou pasta selecionado para exclusão.", "warning");
      return;
    }

    const selectedItems = selectedFiles.map((fullPath) => {
      return files.find((f) => f.fullPath === fullPath) || { fullPath, type: "folder" };
    });
    const hasFolders = selectedItems.some((f) => f.type === "folder");
    const hasFiles = selectedItems.some((f) => f.type === "file");
    let itemDescription = "item(ns)";
    if (hasFolders && !hasFiles) itemDescription = "pasta(s)";
    else if (hasFiles && !hasFolders) itemDescription = "arquivo(s)";

    const confirmMessage = selectedFiles.length === 1 && hasFolders
      ? "Tem certeza que deseja excluir a pasta selecionada e todo o seu conteúdo? Esta ação não pode ser desfeita."
      : `Tem certeza que deseja excluir ${selectedFiles.length} ${itemDescription} selecionado(s)? Esta ação não pode ser desfeita.`;

    const deleteAction = async () => {
      try {
        setIsDeleting(true);
        setLoadingFiles(true);

        for (const fullPath of selectedFiles) {
          const fileObj = files.find((f) => f.fullPath === fullPath);

          if (fileObj && fileObj.type === "folder") {
            await deleteFolderRecursively(storage, db, fullPath, fileObj.id);
          } else if (fileObj && fileObj.id) {
            try {
              // Delete from Firestore
              await deleteDoc(doc(db, "files", fileObj.id));

              // Delete public shares
              try {
                const qShares = query(
                  collection(db, "public_shares"),
                  where("id", "==", fileObj.id)
                );
                const sharesSnapshot = await getDocs(qShares);
                for (const shareDoc of sharesSnapshot.docs) {
                  await deleteDoc(doc(db, "public_shares", shareDoc.id));
                }
              } catch (shareErr) {
                console.warn("Failed to delete public shares:", fileObj.id, shareErr);
              }

              // Delete original file from Storage
              if (storage && fileObj.fullPath) {
                try {
                  const fileRef = ref(storage, fileObj.fullPath);
                  await deleteObject(fileRef);
                } catch (storageErr) {
                  if (storageErr.code !== "storage/object-not-found") {
                    console.warn("Storage delete error:", storageErr);
                  }
                }
              }

              // Delete thumbnail if exists
              if (storage && fileObj.thumbnailUrl) {
                try {
                  const thumbnailPath = `thumbnails/${
                    fileObj.folder ? `${fileObj.folder}/` : ""
                  }${generateThumbnailFilename(fileObj.name)}`;
                  const thumbnailRef = ref(storage, thumbnailPath);
                  await deleteObject(thumbnailRef);
                } catch (thumbnailError) {
                  if (thumbnailError.code !== "storage/object-not-found") {
                    console.warn("Failed to delete thumbnail:", thumbnailError);
                  }
                }
              }
            } catch (error) {
              console.error("Error deleting file:", fileObj.name, error);
              throw error;
            }
          } else {
            // Fallback for folders or items not in current memory state
            await deleteFolderRecursively(storage, db, fullPath);
          }
        }

        const count = selectedFiles.length;
        setSelectedFiles([]);
        folderCacheRef.current.clear();
        await listAllFiles(currentFolder, { forceRefresh: true });

        showSnackbar(
          `${count} ${itemDescription} excluído(s) com sucesso!`,
          "success",
        );
      } catch (error) {
        console.error("Erro ao excluir arquivo(s):", error);
        showSnackbar("Erro ao excluir: " + (error.message || "Erro desconhecido"), "error");
      } finally {
        setIsDeleting(false);
        setLoadingFiles(false);
      }
    };

    showConfirmation(
      "Confirmar Exclusão",
      confirmMessage,
      deleteAction,
      "error",
    );
  }, [selectedFiles, files, showSnackbar, showConfirmation, db, storage, currentFolder, listAllFiles]);

  const handleDeleteItem = useCallback(
    (item) => {
      if (!item) return;
      const isFolder = item.type === "folder";
      const itemType = isFolder ? "a pasta" : "o arquivo";
      const confirmMessage = isFolder
        ? `Tem certeza que deseja excluir ${itemType} "${item.name}" e todo o seu conteúdo? Esta ação não pode ser desfeita.`
        : `Tem certeza que deseja excluir ${itemType} "${item.name}"? Esta ação não pode ser desfeita.`;

      const deleteAction = async () => {
        try {
          setIsDeleting(true);
          setLoadingFiles(true);

          if (isFolder) {
            await deleteFolderRecursively(storage, db, item.fullPath, item.id);
          } else {
            if (item.id) {
              await deleteDoc(doc(db, "files", item.id));
              try {
                const qShares = query(
                  collection(db, "public_shares"),
                  where("id", "==", item.id)
                );
                const sharesSnapshot = await getDocs(qShares);
                for (const shareDoc of sharesSnapshot.docs) {
                  await deleteDoc(doc(db, "public_shares", shareDoc.id));
                }
              } catch (shareErr) {}
            }
            if (storage && item.fullPath) {
              try {
                await deleteObject(ref(storage, item.fullPath));
              } catch (e) {}
            }
            if (storage && item.thumbnailUrl) {
              try {
                const thumbnailPath = `thumbnails/${
                  item.folder ? `${item.folder}/` : ""
                }${generateThumbnailFilename(item.name)}`;
                await deleteObject(ref(storage, thumbnailPath));
              } catch (e) {}
            }
          }

          setSelectedFiles((prev) => prev.filter((p) => p !== item.fullPath));
          folderCacheRef.current.clear();
          await listAllFiles(currentFolder, { forceRefresh: true });
          showSnackbar(
            `${isFolder ? "Pasta" : "Arquivo"} excluído(a) com sucesso!`,
            "success",
          );
        } catch (error) {
          console.error("Erro ao excluir item:", error);
          showSnackbar("Erro ao excluir: " + (error.message || "Erro desconhecido"), "error");
        } finally {
          setIsDeleting(false);
          setLoadingFiles(false);
        }
      };

      showConfirmation(
        "Confirmar Exclusão",
        confirmMessage,
        deleteAction,
        "error",
      );
    },
    [db, storage, currentFolder, listAllFiles, showSnackbar, showConfirmation]
  );

  const handleRename = useCallback(async (file, newName) => {
    if (!file || !newName.trim()) {
      showSnackbar("O nome não pode ser vazio.", "warning");
      return;
    }

    const nameExists = await checkNameExists(db, file.folder, newName);
    if (nameExists) {
      showSnackbar(
        `Já existe um arquivo ou pasta com o nome "${newName}"`,
        "error",
      );
      return;
    }

    const newPath = file.folder ? `${file.folder}/${newName}` : newName;

    setRenaming(true);
    try {
      if (file.type === "file") {
        if (file.fullPath === newPath) {
          setRenameDialogOpen(false);
          return;
        }
        const oldFileRef = ref(storage, file.fullPath);

        // Download the file's content
        const blob = await getBlob(oldFileRef);

        // Upload the content to the new path
        const newFileRef = ref(storage, newPath);
        await uploadBytes(newFileRef, blob);

        // Get the download URL of the new file
        const newUrl = await getDownloadURL(newFileRef);

        // Update the document in Firestore
        const fileDocRef = doc(db, "files", file.id);
        await updateDoc(fileDocRef, {
          name: newName,
          fullPath: newPath,
          originalUrl: newUrl,
        });

        // Delete the old file
        await deleteObject(oldFileRef);
      } else if (file.type === "folder") {
        await renameFolder(storage, db, file, newName);
      }

      showSnackbar("Renomeado com sucesso!", "success");
      folderCacheRef.current.clear();
      listAllFiles(currentFolder, { forceRefresh: true });
      setRenameDialogOpen(false);
    } catch (error) {
      console.error("Erro ao renomear:", error);
      showSnackbar("Erro ao renomear: " + error.message, "error");
    } finally {
      setRenaming(false);
    }
  }, [db, storage, currentFolder, listAllFiles, showSnackbar]);

  const handleMoveFile = useCallback(
    async (file, targetFolder) => {
      if (!file) return;
      try {
        const result = await moveFile(storage, db, file, targetFolder, folderPrefix);
        showSnackbar(
          `Arquivo movido com sucesso para "${result.targetFolderName}"!`,
          "success",
        );
        folderCacheRef.current.clear();
        await listAllFiles(currentFolder, { forceRefresh: true });
        return result;
      } catch (error) {
        console.error("Erro ao mover arquivo:", error);
        showSnackbar("Erro ao mover arquivo: " + error.message, "error");
        throw error;
      }
    },
    [storage, db, folderPrefix, currentFolder, listAllFiles, showSnackbar],
  );

  const sharePublicly = useCallback(async (fileOrFolder) => {
    try {
      const isPublic = fileOrFolder.public || false;

      if (isPublic) {
        // Lógica para remover o compartilhamento público
        await updateDoc(
          doc(
            db,
            fileOrFolder.type === "file" ? "files" : "folders",
            fileOrFolder.id,
          ),
          {
            public: false,
          },
        );

        const q = query(
          collection(db, "public_shares"),
          where("id", "==", fileOrFolder.id),
        );
        const querySnapshot = await getDocs(q);
        querySnapshot.forEach(async (doc) => {
          await deleteDoc(doc.ref);
        });

        showSnackbar(
          "Compartilhamento público removido com sucesso!",
          "success",
        );
      } else {
        // Lógica para adicionar o compartilhamento público
        await updateDoc(
          doc(
            db,
            fileOrFolder.type === "file" ? "files" : "folders",
            fileOrFolder.id,
          ),
          {
            public: true,
          },
        );

        await addDoc(collection(db, "public_shares"), {
          id: fileOrFolder.id,
          type: fileOrFolder.type,
          name: fileOrFolder.name,
          fullPath: fileOrFolder.fullPath,
          tenant: tenant || "default",
          createdBy: auth?.currentUser?.email || "unknown",
          createdAt: new Date(),
        });

        showSnackbar(
          "Arquivo compartilhado publicamente com sucesso!",
          "success",
        );
      }

      folderCacheRef.current.clear();
      listAllFiles(currentFolder, { forceRefresh: true });
    } catch (error) {
      console.error("Erro ao compartilhar publicamente:", error);
      showSnackbar("Erro ao compartilhar: " + error.message, "error");
    }
  }, [db, tenant, auth, currentFolder, listAllFiles, showSnackbar]);

  const setFolderCover = useCallback(
    async (folderOrPath, fileOrUrl, options = {}) => {
      let folderId = folderOrPath?.id;
      let folderName = folderOrPath?.name;
      const coverPosition =
        options?.coverPosition ||
        (typeof fileOrUrl === "object" && fileOrUrl?.coverPosition) ||
        (typeof folderOrPath === "object" && folderOrPath?.coverPosition) ||
        "center 50%";

      if (!folderId) {
        const folderPath =
          typeof folderOrPath === "string"
            ? folderOrPath
            : folderOrPath?.fullPath;
        const cleanPath = (folderPath || "").replace(/^\/+|\/+$/g, "");
        if (cleanPath) {
          try {
            const q = query(
              collection(db, "folders"),
              where("fullPath", "==", cleanPath),
              limit(1),
            );
            const snap = await getDocs(q);
            if (!snap.empty) {
              folderId = snap.docs[0].id;
              folderName = snap.docs[0].data()?.name || cleanPath;
            } else {
              const namePart = cleanPath.split("/").pop();
              const folderPart = cleanPath.split("/").slice(0, -1).join("/");
              const qFallback = query(
                collection(db, "folders"),
                where("name", "==", namePart),
                where("folder", "==", folderPart),
                limit(1),
              );
              const fallbackSnap = await getDocs(qFallback);
              if (!fallbackSnap.empty) {
                folderId = fallbackSnap.docs[0].id;
                folderName = fallbackSnap.docs[0].data()?.name || namePart;
              }
            }
          } catch (e) {
            console.error("Error finding folder by path:", e);
          }
        }
      }

      if (!folderId) {
        showSnackbar("Pasta não encontrada para definir capa.", "warning");
        return;
      }

      const coverUrl =
        typeof fileOrUrl === "string"
          ? fileOrUrl
          : fileOrUrl?.thumbnailUrl || fileOrUrl?.originalUrl;

      if (!coverUrl) {
        showSnackbar("Arquivo inválido para capa de pasta.", "warning");
        return;
      }

      try {
        const folderDocRef = doc(db, "folders", folderId);
        await updateDoc(folderDocRef, {
          thumbnailUrl: coverUrl,
          coverPosition: coverPosition,
          updatedAt: new Date(),
        });
        showSnackbar(
          folderName
            ? `Capa da pasta "${folderName}" atualizada com sucesso!`
            : "Capa da pasta atualizada com sucesso!",
          "success",
        );
        folderCacheRef.current.clear();
        await listAllFiles(currentFolder, { forceRefresh: true });
      } catch (error) {
        console.error("Erro ao definir a capa da pasta:", error);
        showSnackbar(
          "Erro ao definir a capa da pasta: " + error.message,
          "error",
        );
      }
    },
    [db, currentFolder, listAllFiles, showSnackbar],
  );

  const removeFolderCover = useCallback(
    async (folderOrPath) => {
      let folderId = folderOrPath?.id;
      let folderName = folderOrPath?.name;

      if (!folderId) {
        const folderPath =
          typeof folderOrPath === "string"
            ? folderOrPath
            : folderOrPath?.fullPath;
        const cleanPath = (folderPath || "").replace(/^\/+|\/+$/g, "");
        if (cleanPath) {
          try {
            const q = query(
              collection(db, "folders"),
              where("fullPath", "==", cleanPath),
              limit(1),
            );
            const snap = await getDocs(q);
            if (!snap.empty) {
              folderId = snap.docs[0].id;
              folderName = snap.docs[0].data()?.name || cleanPath;
            } else {
              const namePart = cleanPath.split("/").pop();
              const folderPart = cleanPath.split("/").slice(0, -1).join("/");
              const qFallback = query(
                collection(db, "folders"),
                where("name", "==", namePart),
                where("folder", "==", folderPart),
                limit(1),
              );
              const fallbackSnap = await getDocs(qFallback);
              if (!fallbackSnap.empty) {
                folderId = fallbackSnap.docs[0].id;
                folderName = fallbackSnap.docs[0].data()?.name || namePart;
              }
            }
          } catch (e) {
            console.error("Error finding folder by path:", e);
          }
        }
      }

      if (!folderId) {
        showSnackbar("Pasta não encontrada para remover capa.", "warning");
        return;
      }

      try {
        const folderDocRef = doc(db, "folders", folderId);
        await updateDoc(folderDocRef, {
          thumbnailUrl: deleteField(),
          coverPosition: deleteField(),
          updatedAt: new Date(),
        });
        showSnackbar(
          folderName
            ? `Capa da pasta "${folderName}" removida com sucesso!`
            : "Capa da pasta removida com sucesso!",
          "success",
        );
        folderCacheRef.current.clear();
        await listAllFiles(currentFolder, { forceRefresh: true });
      } catch (error) {
        console.error("Erro ao remover a capa da pasta:", error);
        showSnackbar(
          "Erro ao remover a capa da pasta: " + error.message,
          "error",
        );
      }
    },
    [db, currentFolder, listAllFiles, showSnackbar],
  );

  const contextValue = useMemo(
    () => ({
      uploading,
      renameDialogOpen,
      setRenameDialogOpen,
      renameFile,
      setRenameFile,
      handleRename,
      renaming,
      newFolderDialogOpen,
      setNewFolderDialogOpen,
      handleFileUpload,
      snackbarOpen,
      snackbarMessage,
      snackbarSeverity,
      showSnackbar,
      setSnackbarOpen,
      currentFolder,
      setCurrentFolder,
      uploadProgress,
      uploadFileName,
      uploadModalOpen,
      setUploadModalOpen,
      uploadQueue,
      setUploadQueue,
      isUploadMinimized,
      setIsUploadMinimized,
      files,
      loadingFiles,
      listAllFiles,
      selectedFiles,
      setSelectedFiles,
      handleFileSelection,
      handleDeleteSelectedFiles,
      confirmationModalOpen,
      setConfirmationModalOpen,
      confirmationModalConfig,
      handleSelectAll,
      searchResults,
      loadingSearch,
      searchFilesAndFolders,
      setFolderCover,
      removeFolderCover,
      sharePublicly,
      downloadSelectedAsZip,
      isZipping,
      zipProgress,
      zipError,
      isDeleting,
      handleDeleteItem,
      handleMoveFile,
      moveFile,
    }),
    [
      uploading,
      newFolderDialogOpen,
      setNewFolderDialogOpen,
      handleFileUpload,
      snackbarOpen,
      snackbarMessage,
      snackbarSeverity,
      showSnackbar,
      setSnackbarOpen,
      currentFolder,
      setCurrentFolder,
      uploadProgress,
      uploadFileName,
      uploadModalOpen,
      setUploadModalOpen,
      uploadQueue,
      isUploadMinimized,
      files,
      loadingFiles,
      listAllFiles,
      selectedFiles,
      setSelectedFiles,
      handleFileSelection,
      handleDeleteSelectedFiles,
      confirmationModalOpen,
      setConfirmationModalOpen,
      confirmationModalConfig,
      handleSelectAll,
      renameDialogOpen,
      setRenameDialogOpen,
      renameFile,
      setRenameFile,
      handleRename,
      renaming,
      searchResults,
      loadingSearch,
      searchFilesAndFolders,
      setFolderCover,
      removeFolderCover,
      sharePublicly,
      tenant,
      app, // Add app to dependencies
      downloadSelectedAsZip,
      isZipping,
      zipProgress,
      zipError,
      isDeleting,
      handleDeleteItem,
      handleMoveFile,
    ],
  );

  return (
    <FileActionsContext.Provider value={contextValue}>
      {children}
    </FileActionsContext.Provider>
  );
};

export const useFileActions = () => {
  return useContext(FileActionsContext);
};
